import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { UserManagementService } from "./user-management/user-management.service";
import { UserRolesService } from "./user-roles.service";

@Injectable({
  providedIn: "root",
})
export class AuthAccessService {
  private readonly publicRoutes = new Set([
    "/login",
    "/signup",
    "/forgotpass",
    "/privacy",
    "/terms",
    // PurifAI SSO redeem: the ticket IS the credential, so the route must run
    // unauthenticated — guarding it would bounce every handoff to /login.
    "/sso",
  ]);

  constructor(
    private userService: UserManagementService,
    private router: Router,
    private userRolesService: UserRolesService
  ) {}

  canAccess(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    const userDetail = this.userService.getUserDetail();
    const hasAuthSession = this.userService.hasValidAuthSession();
    const pathOnly = this.normalizeUrl(state.url);

    if (!hasAuthSession || !userDetail?.id) {
      if (this.publicRoutes.has(pathOnly)) {
        return true;
      }

      return this.router.createUrlTree(["/login"], {
        queryParams: { returnUrl: state.url },
      });
    }

    if (pathOnly === "/login") {
      return this.router.createUrlTree(["/"]);
    }

    const configuredRoles = this.extractRoles(route);
    if (configuredRoles?.length) {
      return configuredRoles.includes(String(userDetail.role))
        ? true
        : this.router.createUrlTree(["/"]);
    }

    const routePolicy = this.userRolesService.getRoutePolicy(pathOnly);
    if (!routePolicy) {
      return true;
    }

    return this.userRolesService.isAuthenticate(pathOnly, userDetail.role)
      ? true
      : this.router.createUrlTree(["/"]);
  }

  private extractRoles(route: ActivatedRouteSnapshot): string[] {
    const routeRoles = route.data?.["roles"];
    if (!Array.isArray(routeRoles)) {
      return [];
    }

    return routeRoles.map((role) => String(role));
  }

  private normalizeUrl(url: string): string {
    const [pathOnly] = url.split("?");
    const normalizedPath = pathOnly.replace(/\/+$/, "");
    return normalizedPath || "/";
  }
}
